import { ApolloClient, gql } from 'apollo-boost';

export interface Feature {
  dbId: number;
  name: string;
  description: string | null;
  value: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureUserOverride {
  dbId: number;
  featureId: number;
  email: string;
  value: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AllFeaturesResponse {
  allFeatures: Feature[];
}

export interface FeatureUserOverridesResponse {
  featureUserOverrides: FeatureUserOverride[];
}

const allFeaturesQuery = gql`
  query AllFeatures {
    allFeatures {
      dbId
      name
      description
      value
      enabled
      createdAt
      updatedAt
    }
  }
`;

export const getAllFeatures = (client: ApolloClient<object>) =>
  client.query<AllFeaturesResponse>({
    query: allFeaturesQuery,
    fetchPolicy: 'network-only',
  });

const featureUserOverridesQuery = gql`
  query FeatureUserOverrides($featureId: Int!) {
    featureUserOverrides(featureId: $featureId) {
      dbId
      featureId
      email
      value
      enabled
      createdAt
      updatedAt
    }
  }
`;

export const getFeatureUserOverrides = (
  client: ApolloClient<object>,
  featureId: number,
) =>
  client.query<FeatureUserOverridesResponse>({
    query: featureUserOverridesQuery,
    variables: { featureId },
    fetchPolicy: 'network-only',
  });

const newFeatureMutation = gql`
  mutation NewFeature(
    $name: String!
    $description: String
    $value: Boolean
    $enabled: Boolean
  ) {
    newFeature(
      name: $name
      description: $description
      value: $value
      enabled: $enabled
    ) {
      success
      featureId
      error
    }
  }
`;

export interface NewFeatureResponse {
  newFeature: {
    success: boolean;
    featureId: number | null;
    error: string | null;
  };
}

export const createFeature = (
  client: ApolloClient<object>,
  variables: {
    name: string;
    description?: string | null;
    value?: boolean;
    enabled?: boolean;
  },
) =>
  client.mutate<NewFeatureResponse>({
    mutation: newFeatureMutation,
    variables,
  });

const updateFeatureMutation = gql`
  mutation UpdateFeature(
    $id: Int!
    $name: String
    $description: String
    $value: Boolean
    $enabled: Boolean
  ) {
    updateFeature(
      id: $id
      name: $name
      description: $description
      value: $value
      enabled: $enabled
    ) {
      success
      error
    }
  }
`;

export interface UpdateFeatureResponse {
  updateFeature: { success: boolean; error: string | null };
}

export const updateFeature = (
  client: ApolloClient<object>,
  variables: {
    id: number;
    name?: string;
    description?: string | null;
    value?: boolean;
    enabled?: boolean;
  },
) =>
  client.mutate<UpdateFeatureResponse>({
    mutation: updateFeatureMutation,
    variables,
  });

const setFeatureUserMutation = gql`
  mutation SetFeatureUser(
    $featureId: Int!
    $email: String!
    $value: Boolean!
    $enabled: Boolean
  ) {
    setFeatureUser(
      featureId: $featureId
      email: $email
      value: $value
      enabled: $enabled
    ) {
      success
      featureUserId
      error
    }
  }
`;

export interface SetFeatureUserResponse {
  setFeatureUser: {
    success: boolean;
    featureUserId: number | null;
    error: string | null;
  };
}

export const setFeatureUser = (
  client: ApolloClient<object>,
  variables: {
    featureId: number;
    email: string;
    value: boolean;
    enabled?: boolean;
  },
) =>
  client.mutate<SetFeatureUserResponse>({
    mutation: setFeatureUserMutation,
    variables,
  });

const deleteFeatureMutation = gql`
  mutation DeleteFeature($id: Int!) {
    deleteFeature(id: $id) {
      success
      error
    }
  }
`;

export interface DeleteFeatureResponse {
  deleteFeature: { success: boolean; error: string | null };
}

export const deleteFeature = (client: ApolloClient<object>, id: number) =>
  client.mutate<DeleteFeatureResponse>({
    mutation: deleteFeatureMutation,
    variables: { id },
  });

const deleteFeatureUserMutation = gql`
  mutation DeleteFeatureUser($id: Int!) {
    deleteFeatureUser(id: $id) {
      success
      error
    }
  }
`;

export interface DeleteFeatureUserResponse {
  deleteFeatureUser: { success: boolean; error: string | null };
}

export const deleteFeatureUser = (client: ApolloClient<object>, id: number) =>
  client.mutate<DeleteFeatureUserResponse>({
    mutation: deleteFeatureUserMutation,
    variables: { id },
  });

export type PlatformCode = 'ti' | 'cn' | 'bb';

export interface FeaturePlatformOverride {
  dbId: number;
  featureId: number;
  platform: PlatformCode;
  value: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeaturePlatformOverridesResponse {
  featurePlatformOverrides: FeaturePlatformOverride[];
}

const featurePlatformOverridesQuery = gql`
  query FeaturePlatformOverrides($featureId: Int!) {
    featurePlatformOverrides(featureId: $featureId) {
      dbId
      featureId
      platform
      value
      enabled
      createdAt
      updatedAt
    }
  }
`;

export const getFeaturePlatformOverrides = (
  client: ApolloClient<object>,
  featureId: number,
) =>
  client.query<FeaturePlatformOverridesResponse>({
    query: featurePlatformOverridesQuery,
    variables: { featureId },
    fetchPolicy: 'network-only',
  });

const setFeaturePlatformMutation = gql`
  mutation SetFeaturePlatform(
    $featureId: Int!
    $platform: String!
    $value: Boolean!
    $enabled: Boolean
  ) {
    setFeaturePlatform(
      featureId: $featureId
      platform: $platform
      value: $value
      enabled: $enabled
    ) {
      success
      featurePlatformId
      error
    }
  }
`;

export interface SetFeaturePlatformResponse {
  setFeaturePlatform: {
    success: boolean;
    featurePlatformId: number | null;
    error: string | null;
  };
}

export const setFeaturePlatform = (
  client: ApolloClient<object>,
  variables: {
    featureId: number;
    platform: PlatformCode;
    value: boolean;
    enabled?: boolean;
  },
) =>
  client.mutate<SetFeaturePlatformResponse>({
    mutation: setFeaturePlatformMutation,
    variables,
  });

const deleteFeaturePlatformMutation = gql`
  mutation DeleteFeaturePlatform($id: Int!) {
    deleteFeaturePlatform(id: $id) {
      success
      error
    }
  }
`;

export interface DeleteFeaturePlatformResponse {
  deleteFeaturePlatform: { success: boolean; error: string | null };
}

export const deleteFeaturePlatform = (
  client: ApolloClient<object>,
  id: number,
) =>
  client.mutate<DeleteFeaturePlatformResponse>({
    mutation: deleteFeaturePlatformMutation,
    variables: { id },
  });
